/**
 * Infinite scroll for collection pages.
 *
 * Progressive enhancement over Shopify's paginated collection grid. Each
 * [data-infinite-scroll] container carries data-section-id and
 * data-grid-target. When it scrolls into view we fetch the next page through
 * the Section Rendering API and append its product cards to the live grid.
 * If JS fails, the normal pagination links still work.
 *
 * ---------------------------------------------------------------------------
 * RE-ARMING THE OBSERVER AFTER AN APPEND
 * ---------------------------------------------------------------------------
 * IntersectionObserver fires on TRANSITIONS - when an element crosses the
 * threshold - not continuously while it stays intersecting. With a 600px
 * rootMargin the sentinel is usually still "in view" immediately after a page
 * is appended, so no further callback ever arrives and loading stops dead.
 *
 * Symptom: the grid stalls with "Still loading out of stock products..." on
 * screen and nothing more happens, because the empty response that sets
 * exhausted - and therefore reveals the sold-out products - only comes from a
 * fetch that never runs. Scrolling up and back down fixes it, because that
 * makes the sentinel leave and re-enter, producing a fresh transition.
 *
 * After every successful append the observer is therefore re-armed by
 * unobserving and re-observing the sentinel, which delivers a fresh callback
 * with the current intersection state. If it is still in view, the next page
 * loads immediately; if not, normal scrolling takes over again.
 *
 * This is also why a short collection could previously never reveal its
 * sold-out products at all: on a page where the sentinel starts inside the
 * root margin and never leaves it, exactly one callback ever fired.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS HOLDS NO CACHED DOM OR PAGE STATE
 * ---------------------------------------------------------------------------
 * Products were disappearing after using the availability filter - hide
 * out-of-stock, show it again, and items from page 2 (Luca Nalu among them)
 * were gone. Four faults compounded, all stale state:
 *
 *  1. The grid element was cached at init. AJAX filtering REPLACES the grid
 *     node - measured isConnected === false afterwards. Appends went into a
 *     detached element and were silently thrown away.
 *  2. The page counter advanced anyway, so page 2 was consumed while invisible
 *     and the next real append started at page 3. That is the missing products.
 *  3. data-next-url is written by Liquid at first render and never gains the
 *     filter parameters - measured still page=2 with zero filter params after
 *     filtering, so scrolling fetched the UNFILTERED page 2.
 *  4. dataset.infiniteScrollReady made init one-shot, and the container node
 *     survives filtering, so the stale state was never refreshed.
 *
 * So: the grid is resolved by id at the moment of use, the next page number is
 * derived from the live URL so it inherits filters and sort, and the counter
 * only advances after cards actually land in a connected grid. A mid-flight
 * guard discards a response if the grid was swapped while it was in the air.
 *
 * ---------------------------------------------------------------------------
 * EVENTS - sold-out-sort.js depends on these
 * ---------------------------------------------------------------------------
 *   infinite-scroll:products-appended  cards were added to the live grid
 *   infinite-scroll:exhausted          no further pages exist for this query
 *   infinite-scroll:reset              filters changed; paging starts over
 *
 * sold-out-sort keeps sold-out cards hidden while paging and reveals them on
 * 'exhausted', so it needs the difference between "more coming" and "that is
 * everything". Without 'exhausted' those products stay hidden, so it is
 * dispatched on every terminal path: an empty response, a zero-card append, a
 * fetch error, and the case where the page has no pagination container.
 */
(function () {
  'use strict';

  if (window.__infiniteScrollInit) return;
  window.__infiniteScrollInit = true;

  document.documentElement.classList.add('js-infinite-scroll');

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function gridFor(container) {
    var id = container.getAttribute('data-grid-target');
    return id ? document.getElementById(id) : null;
  }

  function currentPage() {
    var p = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  }

  /* Built from the live URL so active filters and sort come along. */
  function pageUrl(page, sectionId) {
    var url = new URL(window.location.pathname, window.location.origin);
    new URLSearchParams(window.location.search).forEach(function (value, key) {
      if (key && key !== 'page' && key !== 'section_id') url.searchParams.append(key, value);
    });
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function exhaust(state) {
    if (state.exhausted) return;
    state.exhausted = true;
    emit('infinite-scroll:exhausted');
  }

  /* A fresh observe() delivers a new callback with the current intersection
     state - see RE-ARMING THE OBSERVER above. */
  function rearm(container, state) {
    if (!state.observer || state.exhausted) return;
    state.observer.unobserve(container);
    state.observer.observe(container);
  }

  function load(container, state) {
    if (state.loading || state.exhausted) return;

    var grid = gridFor(container);
    if (!grid) return;

    if (grid !== state.grid) {
      state.grid = grid;
      state.page = currentPage();
      state.exhausted = false;
    }

    var sectionId = container.getAttribute('data-section-id') || '';
    if (!sectionId) return;

    state.loading = true;
    state.rearm = false;
    var spinner = container.querySelector('[data-infinite-scroll-spinner]');
    if (spinner) spinner.hidden = false;

    var requestedFor = grid;
    var next = state.page + 1;

    fetch(pageUrl(next, sectionId), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Infinite scroll: status ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var live = gridFor(container);
        if (!live || !live.isConnected || live !== requestedFor) return;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.getElementById(container.getAttribute('data-grid-target'));

        if (!incoming || !incoming.firstElementChild) {
          exhaust(state);
          return;
        }

        var added = 0;
        while (incoming.firstElementChild) {
          live.appendChild(incoming.firstElementChild);
          added++;
        }

        if (added > 0) {
          state.page = next;
          state.rearm = true;
          emit('infinite-scroll:products-appended', { grid: live, added: added });
        } else {
          exhaust(state);
        }
      })
      .catch(function (error) {
        console.error('Infinite scroll: failed to load next page.', error);
        exhaust(state);
      })
      .finally(function () {
        state.loading = false;
        if (spinner) spinner.hidden = true;
        /* After loading is cleared, so the fresh callback can act on it. */
        if (state.rearm) {
          state.rearm = false;
          rearm(container, state);
        }
      });
  }

  function initContainer(container) {
    if (container.__infiniteScrollState) return;

    var state = {
      grid: null,
      page: currentPage(),
      loading: false,
      exhausted: false,
      rearm: false,
      observer: null
    };
    container.__infiniteScrollState = state;

    state.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) load(container, state);
      });
    }, { rootMargin: '600px 0px 600px 0px' });

    state.observer.observe(container);
  }

  function initAll() {
    var containers = document.querySelectorAll('[data-infinite-scroll]');

    /* No pagination here - anything waiting on 'exhausted' must be told now. */
    if (!containers.length) {
      if (document.querySelector('.product-grid')) emit('infinite-scroll:exhausted');
      return;
    }

    containers.forEach(initContainer);
  }

  function resetAll() {
    emit('infinite-scroll:reset');
    document.querySelectorAll('[data-infinite-scroll]').forEach(function (container) {
      var state = container.__infiniteScrollState;
      if (!state) return initContainer(container);
      state.exhausted = false;
      state.grid = null;
      state.page = currentPage();
      rearm(container, state);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  window.addEventListener('popstate', resetAll);

  var lastSearch = window.location.search;
  window.setInterval(function () {
    if (window.location.search !== lastSearch) {
      lastSearch = window.location.search;
      resetAll();
    }
  }, 300);
})();
